import React, { useState, useEffect } from 'react';
import {
  FaDesktop,
  FaDownload,
  FaSync,
  FaShieldAlt,
  FaWindows,
  FaApple,
  FaLinux,
  FaMobile,
} from 'react-icons/fa';
import { SiUbuntu } from 'react-icons/si';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { downloadDesktopApp } from '../../utils/downloadUtils';

const DesktopAppSection = ({ showHeader = true }) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageError(true);
    img.src = '/Images/aiiamge.png';
  }, []);

  const features = [
    {
      icon: <FaDesktop className="text-lg" />,
      title: t('landing.slideSix.features.crossPlatform.title', 'Cross-Platform Support'),
      description: t('landing.slideSix.features.crossPlatform.description', 'Available on Windows, macOS, and Linux'),
    },
    {
      icon: <FaDownload className="text-lg" />,
      title: t('landing.slideSix.features.offlineAccess.title', 'Offline Access'),
      description: t('landing.slideSix.features.offlineAccess.description', 'Continue learning without an internet connection'),
    },
    {
      icon: <FaSync className="text-lg" />,
      title: t('landing.slideSix.features.autoSync.title', 'Auto-Sync'),
      description: t('landing.slideSix.features.autoSync.description', 'Seamlessly sync your work across all devices'),
    },
    {
      icon: <FaShieldAlt className="text-lg" />,
      title: t('landing.slideSix.features.enhancedSecurity.title', 'Enhanced Security'),
      description: t('landing.slideSix.features.enhancedSecurity.description', 'Local processing for sensitive content'),
    },
  ];

  const platforms = [
    { icon: <FaWindows className="text-2xl" />, name: 'Windows', color: 'text-blue-600 dark:text-blue-400' },
    { icon: <FaApple className="text-2xl" />, name: 'macOS', color: 'text-gray-800 dark:text-gray-300' },
    { icon: <FaLinux className="text-2xl" />, name: 'Linux', color: 'text-amber-600 dark:text-amber-400' },
    { icon: <SiUbuntu className="text-2xl" />, name: 'Ubuntu', color: 'text-orange-500 dark:text-orange-400' },
  ];

  return (
    <main className="w-full bg-white dark:bg-slate-950">
      <div className="relative overflow-hidden py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/30 to-white dark:from-gray-950 dark:via-blue-950/10 dark:to-gray-950" />
        <div className="relative max-w-7xl mx-auto">
          {showHeader && (
            <section className="text-center max-w-3xl mx-auto mb-20">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-full border-2 border-blue-300 dark:border-blue-700 mb-6"
              >
                <FaMobile className="text-blue-600 dark:text-blue-400 text-sm" />
                <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                  {t('landing.slideSix.badge', 'Desktop Application')}
                </span>
              </motion.div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight">
                {t('landing.slideSix.header.title', 'Learn anytime, anywhere')}
              </h2>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('landing.slideSix.header.subtitle', 'Stay focused, study offline, and sync your progress across all your devices')}
              </p>
            </section>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-3xl p-10 shadow-2xl border-2 border-blue-200 dark:border-blue-800">
                <div className="absolute -top-5 left-5 flex flex-wrap gap-2 z-10">
                  {platforms.map((p) => (
                    <div
                      key={p.name}
                      className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 rounded-full text-xs font-bold shadow-lg border-2 border-blue-200 dark:border-blue-700"
                    >
                      <span className={p.color}>{p.icon}</span>
                      <span className="text-gray-700 dark:text-gray-300">{p.name}</span>
                    </div>
                  ))}
                </div>

                <div className="relative h-[450px] sm:h-[550px] rounded-3xl overflow-hidden bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 shadow-xl">
                  {!imageError ? (
                    <>
                      {!imageLoaded && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-800">
                          <div className="w-14 h-14 rounded-full border-4 border-blue-500 border-t-transparent animate-spin mb-4" />
                          <p className="text-gray-500 dark:text-gray-400 text-sm">{t('desktopApp.loading', 'Loading preview…')}</p>
                        </div>
                      )}
                      <img
                        src="/Images/aiiamge.png"
                        alt={t('landing.slideSix.appAlt', 'Desktop app preview')}
                        className={`w-full h-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                      />
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 text-center p-8">
                      <FaDesktop className="text-8xl text-blue-400 dark:text-blue-500 mb-6" />
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('landing.slideSix.appTitle', 'Desktop App')}</h3>
                      <p className="text-gray-600 dark:text-gray-400">{t('landing.slideSix.appPreview', 'Download our desktop app for the best experience')}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-4 tracking-tight">
                  {t('landing.slideSix.whyUse.title', 'Why Use Desktop App?')}
                </h3>
                <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
                  {t('landing.slideSix.whyUse.description', 'A distraction-free way to study and build learning routines—online or offline')}
                </p>
              </div>

              <div className="space-y-4">
                {features.map((f, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + i * 0.1 }}
                    className="flex items-start gap-5 p-6 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border-2 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white shadow-sm">
                      {f.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-lg">{f.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{f.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.03, y: -3 }}
                whileTap={{ scale: 0.97 }}
                onClick={downloadDesktopApp}
                className="w-full inline-flex items-center justify-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold text-lg rounded-2xl shadow-xl shadow-blue-500/30 hover:shadow-2xl transition-all duration-200"
              >
                <FaDownload className="text-xl" />
                {t('landing.slideSix.cta.button', 'Download Desktop App')}
                <span className="text-xl">→</span>
              </motion.button>
            </motion.div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default DesktopAppSection;
